import React, { useEffect, useState } from "react";
import { Formik, Form, FieldArray } from "formik";
import {
  TextField, Button, IconButton, Box, Grid, Typography, Divider, Autocomplete, Stack, InputAdornment
} from "@mui/material";
import { Delete, PictureAsPdf, AddCircleOutline } from "@mui/icons-material";
import * as Yup from "yup";
import { LoadingButton } from "@mui/lab";
import { useNotification } from "../../../../../../Contexts/GlobalNotificationContext";
import { useStyles } from "../../../../../../Theme/Style";
import {
  useSubmitEmbroideryDetailsMutation,
  useUpdateEmbroideryDetailsMutation,
  useGetEmbroideryDetailsQuery,
  useGetOrganizationsAllListQuery,
} from "../../../../../../services/api/embroidery-library";
import { authHeader } from "../../../../../../helpers";
import { apiConstants } from "../../../../../../constants";

const validationSchema = Yup.object({
  organization_id: Yup.string().required("Organization is required"),
  design_name: Yup.string().required("Design Name is required"),
  disk_name: Yup.string().required("Embroidery Disk Number is required"),
  width: Yup.number().required("Width is required"),
  height: Yup.number().required("Height is required"),
  ribbon: Yup.string(),
  logo: Yup.string().nullable(),

});

export const EmbroideryLibraryForm = ({ mode, paramdata, handleErrorAlert }) => {
  const classes = useStyles();
  const { addNotification } = useNotification();

  const { data: organizations = [] } = useGetOrganizationsAllListQuery();
  const [submitEmbroidery] = useSubmitEmbroideryDetailsMutation();
  const [updateEmbroidery] = useUpdateEmbroideryDetailsMutation();
  const { data: embroideryData = {} } = useGetEmbroideryDetailsQuery(
    { id: paramdata?.id },
    { skip: !paramdata?.id }
  );

  const [initialValues, setInitialValues] = useState({
    organization_id: "",
    design_name: "",
    disk_name: "",
    width: "",
    height: "",
    ribbon: "",
    logo: null,
    // removed: logo_image_adult, logo_image_junior
    colorways: [{ name: "", image: null }], // start with one row
    logo_images: [{ name: "", image: null }], // start with one row
  });

  // previews for arrays + pdf
  const [preview, setPreview] = useState({
    logo_pdf: null,
  });

  useEffect(() => {
    
    setInitialValues({
      organization_id: embroideryData.organization_id || "",
      design_name: embroideryData.design_name || "",
      disk_name: embroideryData.disk_name || "",
      width: embroideryData.width || "",
      height: embroideryData.height || "",
      ribbon: embroideryData.ribbon || "",
      logo: embroideryData.logo || null,
    });

    setPreview({
      logo_pdf: embroideryData.logo_url || null,
    });
  }, [embroideryData]);

  const uploadFile = async (file, isPdf = false) => {
    const formData = new FormData();
    formData.append(isPdf ? "file" : "image", file);

    const token = authHeader().Authorization.split(" ")[1];
    const res = await fetch(
      isPdf ? `${apiConstants.API_URL}/file/embroidery` : `${apiConstants.API_URL}/images/embroidery`,
      { method: "POST", headers: { Authorization: "Bearer " + token }, body: formData }
    );
    const data = await res.json();
    
    return data?.data ?? null; // { path, url }
  };

  const onSubmitData = async (values, { setSubmitting }) => {
    try {
      const payload = {
        organization_id: values.organization_id,
        design_name: values.design_name,
        disk_name: values.disk_name,
        width: values.width,
        height: values.height,
        ribbon: values.ribbon,
        logo: values.logo,
      };

      if (mode === "edit" && paramdata?.id) {
        await updateEmbroidery({ id: paramdata.id, data: payload }).unwrap();
        addNotification({ message: "Embroidery updated!", type: "success" });
      } else {
        await submitEmbroidery({ data: payload }).unwrap();
        addNotification({ message: "Embroidery submitted!", type: "success" });
      }
      window.location.href = "/admin/parameter-management/embroidery-library";
    } catch (err) {
      addNotification({ message: "Error uploading", type: "error" });
      handleErrorAlert?.();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={onSubmitData}
    >
      {({ values, errors, touched, setFieldValue, isSubmitting }) => (
        <Form>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* Organization */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Organization</Typography>
              <Autocomplete
                options={organizations}
                getOptionLabel={(o) => o.name_with_id}
                value={organizations.find((o) => o.id === values.organization_id) || null}
                onChange={(_, v) => setFieldValue("organization_id", v?.id || "")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Organization"
                    error={touched.organization_id && Boolean(errors.organization_id)}
                    helperText={touched.organization_id && errors.organization_id}
                  />
                )}
              />
            </Grid>

            {/* Design & Disk */}
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography>Design Name</Typography>
                <TextField
                  fullWidth
                  name="design_name"
                  value={values.design_name}
                  onChange={(e) => setFieldValue("design_name", e.target.value)}
                  error={touched.design_name && Boolean(errors.design_name)}
                  helperText={touched.design_name && errors.design_name}
                />
              </Grid>
              <Grid item xs={6}>
                <Typography>Disk Number</Typography>
                <TextField
                  fullWidth
                  name="disk_name"
                  value={values.disk_name}
                  onChange={(e) => setFieldValue("disk_name", e.target.value)}
                  error={touched.disk_name && Boolean(errors.disk_name)}
                  helperText={touched.disk_name && errors.disk_name}
                />
              </Grid>
            </Grid>

            {/* Measurement */}
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <TextField
                  label="Width"
                  fullWidth
                  name="width"
                  value={values.width}
                  onChange={(e) => setFieldValue("width", e.target.value)}
                  error={touched.width && Boolean(errors.width)}
                  helperText={touched.width && errors.width}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Height"
                  fullWidth
                  name="height"
                  value={values.height}
                  onChange={(e) => setFieldValue("height", e.target.value)}
                  error={touched.height && Boolean(errors.height)}
                  helperText={touched.height && errors.height}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">cm</InputAdornment>,
                  }}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label="Total Stitches"
                  fullWidth
                  name="ribbon"
                  value={values.ribbon}
                  onChange={(e) => setFieldValue("ribbon", e.target.value)}
                />
              </Grid>
            </Grid>

            <Divider />

            {/* PDF */}
            <Grid item xs={12}>
              <Typography variant="subtitle1">Upload Logo (PDF)</Typography>
              <input
                accept="application/pdf"
                style={{ display: "none" }}
                id="logo-pdf-file"
                type="file"
                onChange={async (e) => {
                  const f = e.target.files?.[0];
                  if (!f) return;
                  try {
                    const path = await uploadFile(f, true); // server path
                    
                    // keep saving the server path
                    setFieldValue("logo", path.path);
                    setFieldValue("logo_pdf", path.url);
                    // preview url for inline view
                    setPreview((p) => ({ ...p, logo_pdf: path.url }));
                  } catch {
                    addNotification({ message: "Upload error", type: "error" });
                  }
                }}
              />
              <label htmlFor="logo-pdf-file">
                <Button component="span" startIcon={<PictureAsPdf />}>
                  {values.logo
                    ? typeof values.logo === "string"
                      ? values.logo.split("/").pop()
                      : "PDF Uploaded"
                    : "Select PDF"}
                </Button>
              </label>
              {(preview.logo_pdf || values.logo_pdf) && (                
                <Box mt={2}>
                  <Box
                    sx={{
                      border: "1px solid #bbb",
                      borderRadius: 1,
                      overflow: "hidden",
                      height: 420,
                    }}
                  >
                    <object
                      data={preview.logo_pdf || values.logo_pdf}
                      type="application/pdf"
                      width="100%"
                      height="100%"
                    >
                      <Box p={2}>
                        <Typography variant="body2">
                          PDF preview isn’t available in this browser.{" "}
                          <a href={preview.logo_pdf || values.logo_pdf} target="_blank" rel="noreferrer">
                            Open the PDF
                          </a>
                          .
                        </Typography>
                      </Box>
                    </object>
                  </Box>

                  <Stack direction="row" spacing={1} mt={1}>
                    <Button
                      component="a"
                      href={preview.logo_pdf || values.logo_pdf}
                      target="_blank"
                      rel="noreferrer"
                      startIcon={<PictureAsPdf />}
                    >
                      Open PDF
                    </Button>
                    <IconButton
                      color="error"
                      onClick={() => {
                        setFieldValue("logo", null);
                        setFieldValue("logo_pdf", null);
                        setPreview((p) => ({ ...p, logo_pdf: null }));
                      }}
                      title="Remove PDF"
                    >
                      <Delete />
                    </IconButton>
                  </Stack>
                </Box>
              )}
            </Grid>

           

            {/* Submit */}
            {mode !== "view" && (
              <Box>
                <LoadingButton type="submit" variant="contained" loading={isSubmitting}>
                  Save
                </LoadingButton>
              </Box>
            )}
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default EmbroideryLibraryForm;
